'use client';

import type { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import { Badge, ToggleIconButton } from '../../ui';
import styles from './CommonHeader.module.scss';
import raffleStyles from './CommonHeader.raffle.module.scss';

// biome-ignore lint/style/useNodejsImportProtocol: <explanation>
import type { UrlObject } from 'url';
import type { Route } from 'next';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { RegistrationTracker } from '../../analytics';
import { ActionButton } from '../../form';
import { useIsMenuOpenedAtom } from '../CommonMenu/CommonMenu';

type Props<T extends string> = {
  initialSessionData?: ReturnType<typeof useSession>['data'];
  isOptionNotEntered?: boolean;
  hasPendingEntry?: boolean;
  hasAlertEcItem?: boolean;
  siteName: string;
  logoHref?: Route<T> | UrlObject;
  callbackUrl?: string;
  variant?: 'default' | 'raffle';
};

function CommonHeader<T extends string>({
  initialSessionData,
  isOptionNotEntered = false,
  hasPendingEntry = false,
  hasAlertEcItem = false,
  siteName,
  logoHref = '/',
  // biome-ignore lint/correctness/noUnusedVariables: <explanation>
  callbackUrl = '/',
  variant = 'default',
}: Props<T>) {
  const { data: sessionData = initialSessionData } = useSession();
  const [isMenuOpenedValue, setIsMenuOpened] = useIsMenuOpenedAtom();
  const pathname = usePathname();
  const isCollaboPage = pathname?.includes('collabo');
  const [showRegistrationTracking, setShowRegistrationTracking] = useState(false);

  // variantに応じてスタイルを決定
  const currentStyles = variant === 'raffle' ? raffleStyles : styles;

  const showBadge = (session: Session) => {
    // biome-ignore lint/suspicious/noExplicitAny: Sessionの型定義が拡張されているため
    return (session.user as any)?.unreadMessageBoxCount > 0 || isOptionNotEntered || hasPendingEntry || hasAlertEcItem;
  };

  const handleSignupClick = () => {
    // 会員登録開始トラッキング
    RegistrationTracker.trackRegistrationStart();

    // 外部サイトへの遷移トラッキング
    RegistrationTracker.trackExternalRedirect(pathname);

    // 離脱検知を開始
    setShowRegistrationTracking(true);

    // 外部サイトに遷移 (callbackUrlにエンコードされたpathnameを渡すことで、会員登録後に元の画面に戻るようにする)
    location.href = `${process.env.NEXT_PUBLIC_MAIN_URL}/${process.env.NEXT_PUBLIC_SERVICE_ID}/auth/signup?callbackUrl=${encodeURIComponent(pathname)}`;
  };
  return (
    <>
      {/* 会員登録フロー離脱検知 */}
      {showRegistrationTracking}
      <header className={`${currentStyles.componentStyle} ${isMenuOpenedValue ? currentStyles.menuOpened : ''}`}>
        <div className={`${currentStyles.container} ${isCollaboPage ? currentStyles.collaboPage : ''}`}>
          {!isCollaboPage && (
            <Link href={logoHref} className={currentStyles.logo}>
              <img src='/logo.png' alt={siteName} />
            </Link>
          )}
          {sessionData ? (
            <div className={currentStyles.menuWrapper}>
              <ToggleIconButton isActive={isMenuOpenedValue} onClick={() => setIsMenuOpened(!isMenuOpenedValue)} />
              {showBadge(sessionData) && (
                <div className={currentStyles.menuBadge}>
                  <Badge color='primary' />
                </div>
              )}
            </div>
          ) : (
            <div className={currentStyles.auth}>
              <Link
                href={`/auth/signin?callbackUrl=${encodeURIComponent(pathname)}`}
                className={currentStyles.loginLink}
                data-testid='login-link'
              >
                ログイン
              </Link>
              <div className={currentStyles.signupButton}>
                <ActionButton color='primary' size='xSmall' onClick={handleSignupClick} variant={variant}>
                  会員登録
                </ActionButton>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
}

export default CommonHeader;
